import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

const STATUSES = ["new", "in-progress", "scheduled", "declined", "closed"];

function ToggleChip({ checked, onClick, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
        checked ? "bg-gg-green text-white" : "bg-gg-bg border border-gg-border text-gg-brown hover:border-gg-green"
      }`}
    >
      {label}
    </button>
  );
}

function FieldLabel({ label, children, className = "" }) {
  return (
    <Label className={`block ${className}`}>
      <span className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">{label}</span>
      <div className="mt-1.5">{children}</div>
    </Label>
  );
}

export default function InquiryEditDialog({ inquiry, onClose, onSaved }) {
  const [form, setForm] = useState({
    status: inquiry.status || "new",
    admin_notes: inquiry.admin_notes || "",
    curriculum_delivered: !!inquiry.curriculum_delivered,
    archived: !!inquiry.archived,
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/service-requests/${inquiry.id}`, form);
      toast.success("Inquiry updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!confirm(`Permanently delete this inquiry from ${inquiry.name}? Use Archive instead if you want to keep the record.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/service-requests/${inquiry.id}`);
      toast.success("Inquiry deleted");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-gg-surface max-h-[90vh] overflow-y-auto" data-testid="inquiry-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Edit inquiry · {inquiry.name}</DialogTitle>
          <p className="text-sm text-gg-muted">
            {inquiry.email} {inquiry.phone ? ` · ${inquiry.phone}` : ""}
          </p>
        </DialogHeader>

        <div className="space-y-4">
          <FieldLabel label="Status">
            <select value={form.status} onChange={set("status")} className="w-full h-10 rounded-md border border-gg-border bg-gg-surface px-3 text-sm" data-testid="edit-inquiry-status">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </FieldLabel>

          {inquiry.message && (
            <div className="rounded-lg bg-gg-bg border border-gg-border p-3 text-sm italic text-gg-muted">
              "{inquiry.message}"
            </div>
          )}

          <FieldLabel label="📝 Private admin notes">
            <Textarea value={form.admin_notes} onChange={set("admin_notes")} rows={4} placeholder="Lesson plan, follow-up needed, quoted price…" data-testid="edit-inquiry-notes" />
          </FieldLabel>

          <div className="flex flex-wrap gap-2">
            <ToggleChip
              checked={form.curriculum_delivered}
              onClick={() => set("curriculum_delivered")(!form.curriculum_delivered)}
              label={form.curriculum_delivered ? "✓ Curriculum delivered" : "Curriculum pending"}
              testid="edit-inquiry-curriculum-toggle"
            />
            <ToggleChip
              checked={form.archived}
              onClick={() => set("archived")(!form.archived)}
              label={form.archived ? "✓ Archived" : "Active"}
              testid="edit-inquiry-archive-toggle"
            />
          </div>
        </div>

        <DialogFooter className="mt-4 flex justify-between gap-2">
          <button type="button" onClick={del} disabled={busy} className="btn-pill btn-outline text-[#B24C4C] border-[#B24C4C]/40 hover:bg-[#B24C4C]/10" data-testid="edit-inquiry-delete">
            Delete inquiry
          </button>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-pill btn-outline">Cancel</button>
            <button type="button" onClick={save} disabled={busy} className="btn-pill btn-primary disabled:opacity-60" data-testid="edit-inquiry-save">
              {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
